import Backend from 'App/Dataman-Backend';
import { upperFirst } from './utils/Helpers';
import {
  CAT_PRICE_SOURCE_TYPES,
  CAT_SOCIAL_MEDIA_SCRAPER_CORE_ACCOUNTS,
  CAT_SOCIAL_MEDIA_SCRAPER_CORE_RESULTS,
  CAT_SOCIAL_MEDIA_SCRAPER_CORE_SESSIONS,
} from 'App/utils/Collections';
import { getReport } from './services/reports';
import { dealsAPI } from './api/deals';
import { assistantService } from '@/app/api/assistant';
import { newsletterService } from '@/app/api/newsletter';
import {
  socialMediaScraperService,
  coreAccountsService,
  coreResultsService,
  coreSessionsService,
} from '@/app/api/socialMediaScraper';
import { userSessionsAPI } from 'App/api/userSessions';
import similarProducts from '@/app/api/find-similar';
import ingredientsAndNutrients from '@/app/api/ingredients-and-nutrients';
import ingredientsExtractor from './api/ingredients-extractor';
import { wimsPulseService } from '@/app/api/wimsPulse';
import { apiGet, apiPost, apiPostJSON, apiPut, apiDelete, apiPatch, ApiError } from './api/core/http';
import { eventsApiAdapter } from './api/events';
import { patentsApiAdapter, patentsFilterAdapter } from './api/patents';
import { componentsApiAdapter } from './api/components';
import { favoritesApiAdapter } from './api/favorites';
import { samplesApiAdapter } from './api/samples';
import { proofRequestsApiAdapter } from './api/proof-requests';
import { researchesApiAdapter } from './api/researches';
import { informationSourcesApiAdapter, infoSourcesService } from './api/information-sources';
import { standingReportsApiAdapter } from './api/standing-reports';
import { taxonomyApiAdapter, taxonomyFilterAdapter } from './api/taxonomy';
import { filesApiAdapter, filesFilterAdapter } from './api/files';
import { usersApiAdapter, userService, passwordResetService } from './api/users';
import { patentResearchApiAdapter } from './api/patent-research';
import { commentService } from './api/comments';
import { brandsApiAdapter, brandsFilterAdapter } from './api/brands';
import { trademarksApiAdapter, trademarksFilterAdapter } from './api/trademarks';
import { contactsApiAdapter, contactsFilterAdapter } from './api/contacts';
import { companiesApiAdapter, companiesFilterAdapter } from './api/companies';
import { newsApiAdapter } from './api/news';
import { productsApiAdapter, productsFilterAdapter } from './api/products';

export { apiGet, apiPost, apiPostJSON, apiPut, apiDelete, apiPatch, ApiError };

const { apiUrlSuffix } = Backend.config;
const { apiUrlCountries, apiUrlCountryRegions, apiUrlEconomicRegions } = Backend.config;
const { apiUrlComponentsStats } = Backend.config;
const { apiUrlSearch } = Backend.config;
const { apiUrlProductsTotal, apiUrlCompaniesTotal } = Backend.config;
const { apiUrlCurrencies } = Backend.config;
const {
  apiUrlTrackingInformationContents,
  apiUrlEventTypes,
  apiUrlEventThemes,
  apiUrlEventAccesses,
  apiUrlEventStatus,
  apiUrlAttendingRequests,
  apiUrlPriceSourceTypes,
  apiUrlTrademarksTotal,
  apiUrlPatentsTotal,
  apiUrlContactsTotal,
} = Backend.config;

const Filter = {
  ...taxonomyFilterAdapter,
  ...filesFilterAdapter,
  ...productsFilterAdapter,
  ...brandsFilterAdapter,
  getCountries: (params) => apiGet(apiUrlCountries, params),
  getCountryRegions: (params) => apiGet(apiUrlCountryRegions, params),
  getEconomicRegions: (params) => apiGet(apiUrlEconomicRegions, params),
  ...companiesFilterAdapter,
  ...contactsFilterAdapter,
  ...patentsFilterAdapter,
  ...trademarksFilterAdapter,

  getEventTypes: (params) => apiGet(apiUrlEventTypes, params),
  getEventThemes: (params) => apiGet(apiUrlEventThemes, params),
  getEventAccesses: (params) => apiGet(apiUrlEventAccesses, params),
  getEventStatuses: (params) => apiGet(apiUrlEventStatus, params),
};

const DB = {
  testLogout: Backend.testFail,
  ...productsApiAdapter,
  createTrackingInformationContents: (data) => {
    const formData = new FormData();
    if (data.billing_informations) {
      formData.append('tracking_information_id', data.billing_informations.tracking_information_id);
      formData.append('file', data.billing_informations.file);
    }
    return Backend.post({
      url: apiUrlTrackingInformationContents,
      headers: {
        'Content-type': 'multipart/form-data',
      },
      data: formData,
    });
  },
  getTrackingInformationContents: (params) => apiGet(apiUrlTrackingInformationContents + apiUrlSuffix, { params }),
  deleteTrackingInformationContents: (id) => apiDelete(`${apiUrlTrackingInformationContents}/${id}`),

  getProductCountByCategories: (params) => apiGet(apiUrlProductsTotal, { params }),
  getCompaniesCountByCategories: (params) => apiGet(apiUrlCompaniesTotal, { params }),
  getTrademarksCountByCategories: (params) => apiGet(apiUrlTrademarksTotal, { params }),
  getComponentsCountByCategories: (params) => apiGet(apiUrlComponentsStats, { params }),
  getPatentsCountByCategories: (params) => apiGet(apiUrlPatentsTotal, { params }),
  getContactsCountByCategories: (params) => apiGet(apiUrlContactsTotal, { params }),
  ...patentResearchApiAdapter,
  ...componentsApiAdapter,

  getCurrencies: (params) => {
    return apiGet(apiUrlCurrencies, params);
  },
  getCurrencyItem: (id, params) => apiGet(`${apiUrlCurrencies}/${id}`, { params }),
  ...companiesApiAdapter,

  ...contactsApiAdapter,
  ...trademarksApiAdapter,
  ...brandsApiAdapter,

  ...proofRequestsApiAdapter,
  updateAttendingRequest: (id, data) => apiPatch(`${apiUrlAttendingRequests}/${id}`, { data }),
  ...patentsApiAdapter,
  search: (params) => Backend.get({ params, url: apiUrlSearch }, true),

  ...standingReportsApiAdapter,

  ...taxonomyApiAdapter,
  ...newsApiAdapter,
  ...filesApiAdapter,
  ...samplesApiAdapter,
  ...usersApiAdapter,
  ...researchesApiAdapter,
  ...eventsApiAdapter,
  assistant: assistantService,
  newsletter: newsletterService,
  socialMedia: socialMediaScraperService,
  [CAT_SOCIAL_MEDIA_SCRAPER_CORE_ACCOUNTS]: coreAccountsService,
  [CAT_SOCIAL_MEDIA_SCRAPER_CORE_RESULTS]: coreResultsService,
  [CAT_SOCIAL_MEDIA_SCRAPER_CORE_SESSIONS]: coreSessionsService,
  userSessions: userSessionsAPI,
  similarProducts,
  ingredientsAndNutrients,
  ...dealsAPI,
  [CAT_PRICE_SOURCE_TYPES]: {
    getList: (params) => apiGet(apiUrlPriceSourceTypes, { params }),
  },
  ingredientsExtractor,
  ...favoritesApiAdapter,
  ...informationSourcesApiAdapter,
  wimsPulse: wimsPulseService,
};

const availableCategoriesWithCounters = ['products', 'components', 'companies', 'contacts', 'trademarks', 'patents'];

const getAllCounters = async (categoryId) => {
  const categories = availableCategoriesWithCounters;

  const responses = await Promise.all(
    categories.map((category) => {
      const url = Backend.config[`apiUrl${upperFirst(category)}Total`];
      return Backend.request(url, 'get', { auth: false, params: { category: categoryId } });
    })
  );

  return responses.map(({ data }, index) => {
    const { total: value } = data;
    const label = upperFirst(categories[index]);
    const category = label.toLowerCase();

    return { value, category, label };
  });
};

const Contact = {
  sendOrder: async (product, extra = {}) => {
    return DB.postProductOrder(product.id, extra);
  },
};

const Dataman = {
  getReport, // This function moved to the services folder. Still here for compatibility
  getAllCounters,
  passwordReset: passwordResetService,
  availableCategoriesWithCounters,
  User: userService,
  DB,
  InfoSources: infoSourcesService,
  Filter,
  Comment: commentService,
  Contact,
  setErrorHandler(handler) {
    Backend.onError = handler;
  },
  refreshTokenHandler(handler) {
    Backend.onRefreshToken = handler;
  },
  showDrafts(state = false) {
    Backend.showDrafts = state;
  },
  cancelRequests: Backend.cancel,
};

export default Dataman;
